import { createContext, useEffect, useState, useContext } from "react";
import { API } from "../lib/axios";

export const ProductContext = createContext();

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  const fetchProducts = async (page = 1, append = false, limit = 20) => {
    try {
      setLoading(true);
      
      // For the first page, fetch more products to ensure complete collections
      const actualLimit = page === 1 ? Math.max(limit, 50) : limit;
      
      const res = await API.get(`/products?page=${page}&limit=${actualLimit}`);
      
      // Handle both old and new response formats
      let products, total, hasMore;
      
      if (res.data.pagination) {
        // New paginated format
        products = res.data.data || [];
        total = res.data.pagination.total || 0;
        hasMore = res.data.pagination.hasMore || false;
      } else if (Array.isArray(res.data)) {
        // Old format - direct array
        products = res.data;
        total = res.data.length;
        hasMore = false;
      } else {
        // Fallback
        products = res.data.data || res.data || [];
        total = res.data.total || res.data.pagination?.total || products.length;
        hasMore = res.data.hasMore || res.data.pagination?.hasMore || false;
      }
      
      if (append) {
        setProducts(prev => [...prev, ...products]);
      } else {
        setProducts(products);
      }
      
      setHasMore(hasMore);
      setTotalProducts(total);
      setCurrentPage(page);
    } catch (err) {
      console.error("Failed to fetch products", err);
      // Set empty state on error
      setProducts([]);
      setHasMore(false);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreProducts = () => {
    if (hasMore && !loading) {
      fetchProducts(currentPage + 1, true);
    }
  };

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const res = await API.get("/products/all", {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });
      const allProducts = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setProducts(allProducts);
      setHasMore(false);
      setTotalProducts(allProducts.length);
    } catch (err) {
      console.error("Failed to fetch all products", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Full catalog on mount (paginated merge + dedupe by id) so home grid matches admin count
  useEffect(() => {
    fetchAllProducts();
  }, []);

  return (
    <ProductContext.Provider
      value={{ 
        products, 
        setProducts, 
        fetchProducts, 
        loading, 
        hasMore, 
        currentPage, 
        totalProducts, 
        loadMoreProducts,
        fetchAllProducts
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

// Optional: Custom hook
export const useProducts = () => useContext(ProductContext);
